import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { COCKPIT_DIAGNOSTICS, TELLTALES_DIAGNOSTICS } from "@/constants/constants";
import { MAINTENANCE_SOURCE } from "@/constants/MaintenanceSource";
import { tellTales } from "@/constants/TellTales";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { Preset } from "@/models/Preset";
import { WarningType } from "@/models/Warning";

const now = new Date();

// ── helpers ──

function sd(id: string, data: number) {
  return { diagnostic: { id }, data, dateTime: now, isLastActive: true } as any;
}

function demoEvent(deviceId: string, ruleId: string, hoursAgo = 2) {
  return {
    id: `evt-${deviceId}-${ruleId}`,
    device: { id: deviceId },
    diagnostic: { id: `diag-${ruleId}` },
    rule: { id: ruleId },
    activeFrom: new Date(now.getTime() - hoursAgo * 3600000),
    activeTo: new Date(now.getTime() - (hoursAgo - 1) * 3600000),
    state: "ExceptionEventStateValidId",
    duration: `0${hoursAgo}:00:00`,
  } as any;
}

// Stable rule IDs for seeding
const RULE_IDS = {
  pm: "rule-pm-1",
  pm1: "rule-pm1-1",
  ad: "rule-ad-1",
  ttYellow: "rule-tt-yellow",
  ttRed: "rule-tt-red",
  sb: "rule-sb-1",
};

/** Cockpit + default diagnostics for a vehicle */
function cockpitData(opts: {
  speed: number;
  rpm?: number;
  kw?: number;
  soc?: number;
  fuel?: number;
  adBlue?: number;
  inTemp?: number;
  outTemp?: number;
  engineTemp?: number;
  door1?: number;
  door2?: number;
  door3?: number;
  chargingState?: number;
  ignition?: number;
  odometer?: number;
  parkingBrake?: boolean;
  calibration?: number;
  range?: number;
}) {
  const entries = [
    sd("DiagnosticOutsideTemperatureId", opts.outTemp ?? 14),
    sd("DiagnosticEngineCoolantTemperatureId", opts.engineTemp ?? 85),
    sd("DiagnosticCabInteriorTemperatureId", opts.inTemp ?? 21),
    sd("DiagnosticDoor1StatusId", opts.door1 ?? 0),
    sd("DiagnosticDoor2StatusId", opts.door2 ?? 0),
    sd("aMkXsw2rqV0-pxn4ko_qlnA", opts.door3 ?? 0),
    sd("DiagnosticIgnitionId", opts.ignition ?? 1),
    sd("DiagnosticOdometerId", opts.odometer ?? 80000),
  ];
  if (opts.parkingBrake) entries.push(sd("a8Q59homXrESC9eF_I_JTWA", 1));
  if (opts.rpm != null) entries.push(sd("DiagnosticEngineSpeedId", opts.rpm));
  if (opts.kw != null) entries.push(sd("DiagnosticElectricVehicleBatteryPowerId", opts.kw));
  if (opts.fuel != null) entries.push(sd("DiagnosticFuelLevelId", opts.fuel));
  if (opts.adBlue != null) entries.push(sd("DiagnosticDieselExhaustFluidId", opts.adBlue));
  if (opts.soc != null) entries.push(sd("DiagnosticStateOfChargeId", opts.soc));
  if (opts.chargingState != null) entries.push(sd("DiagnosticElectricVehicleChargingStateId", opts.chargingState));
  if (opts.calibration != null) entries.push(sd("agOXwO-vH0EW66Ry_cjkJrg", opts.calibration));
  if (opts.range != null) entries.push(sd("aFGR6Q9guoUeiaAZI1fD6oQ", opts.range));
  return entries;
}

function device(id: string, name: string, serialNumber = "G9" + id) {
  return {
    id, name, serialNumber, deviceType: "GO9", comment: "", groups: [],
    hardwareId: 0, maxSecondsBetweenLogs: 200, productId: 0,
    timeZoneId: "Europe/Berlin",
    vehicleIdentificationNumber: "WF0XXXGCDX" + id, licensePlate: "",
    activeFrom: new Date("2023-01-01"), activeTo: new Date("2099-12-31"),
  } as any;
}

function archivedDevice(id: string, name: string) {
  const d = device(id, name);
  d.activeTo = new Date("2025-01-01");
  return d;
}

function statusInfo(
  id: string, lat: number, lng: number, speed: number,
  isDriving: boolean, duration: string,
  diagnostics: any[],
  communicating = true,
) {
  return {
    device: { id },
    dateTime: new Date(now.getTime() - Math.random() * 600000),
    latitude: lat, longitude: lng, speed,
    bearing: Math.floor(Math.random() * 360),
    isDeviceCommunicating: communicating,
    isDriving,
    currentStateDuration: duration,
    statusData: diagnostics,
  } as any;
}

function zone(id: string, name: string, r: number, g: number, b: number) {
  return { id, name, displayed: true, fillColor: { r, g, b, a: 200 } } as any;
}

function maintenanceJob(source: string, statusCode: number, severityCode: number) {
  return { maintenanceType: { source }, statusCode, severityCode } as any;
}

const ZONES = {
  depotNord: zone("z1", "Depot Nord", 33, 150, 243),
  depotSued: zone("z2", "Depot Süd", 255, 152, 0),
  zoneA: zone("z3", "Zone A", 76, 175, 80),
  zoneB: zone("z4", "Zone B", 156, 39, 176),
  werkstatt: zone("z5", "Werkstatt", 244, 67, 54),
};

// ── demo drivers ──

const DEMO_USERS: Record<string, any> = {
  driver1: { id: "driver1", name: "m.schmidt@example.com", firstName: "Markus", lastName: "Schmidt" },
  driver2: { id: "driver2", name: "a.mueller@example.com", firstName: "Anna", lastName: "Müller" },
  driver3: { id: "driver3", name: "t.weber@example.com", firstName: "Thomas", lastName: "Weber" },
};

// ── demo preset ──

export const DEMO_PRESET: Preset = {
  id: "demo-preset",
  description: "E-Bus Monitoring",
  columns: [
    { diagnostic: { id: "DiagnosticStateOfChargeId", name: "Ladezustand (SoC)" }, columnSequence: 1 },
    { diagnostic: { id: "DiagnosticEngineCoolantTemperatureId", name: "Kühlmitteltemperatur" }, columnSequence: 2 },
    { diagnostic: { id: "DiagnosticOutsideTemperatureId", name: "Außentemperatur" }, columnSequence: 3 },
    { diagnostic: { id: "aFGR6Q9guoUeiaAZI1fD6oQ", name: "Reichweite" }, columnSequence: 4 },
  ],
};

// ── vehicle data ──

const DEMO_DATA: DeviceStatusOverviewData[] = [
  {
    // Driving, ignition on, AC charging, PM warning — diesel bus
    id: "d1",
    device: device("d1", "BUS-1001"),
    licensePlate: "M-EC 1001",
    statusData: statusInfo("d1", 48.137, 11.576, 42, true, "00:47:12",
      cockpitData({ speed: 42, rpm: 1850, fuel: 68, adBlue: 72, inTemp: 22, outTemp: 16, engineTemp: 88, door1: 0, door2: 0, ignition: 1, odometer: 84523000 }),
    ),
    address: "Marienplatz 1, 80331 München",
    zones: "Zone A",
    ignitionStatus: 1,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: -1,
    isDriving: true,
    faultInstruction: "",
    warnings: [{ type: WarningType.PM, event: demoEvent("d1", RULE_IDS.pm, 5) }],
    chargingInterrupt: false,
    allZones: [ZONES.zoneA],
    chargeEvents: [],
    chargeRate: null,
    maintenance: {},
    latestRemark: "Planmäßig im Einsatz",
    odometer: 84523, odometerAdjustment: 84510,
    driverId: "driver1",
  },
  {
    // Parking brake, ignition off, DC charging — electric bus
    id: "d2",
    device: device("d2", "BUS-1002"),
    licensePlate: "M-EC 1002",
    statusData: statusInfo("d2", 48.142, 11.582, 0, false, "02:15:33",
      cockpitData({ speed: 0, kw: 148, soc: 62, inTemp: 18, outTemp: 12, engineTemp: 30, door1: 0, door2: 0, chargingState: 2, ignition: 0, odometer: 61200000, parkingBrake: true, range: 195 }),
    ),
    address: "Leopoldstraße 42, 80802 München",
    zones: "Depot Nord",
    ignitionStatus: 0,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: 2,
    isDriving: false,
    faultInstruction: "",
    warnings: [],
    chargingInterrupt: false,
    allZones: [ZONES.depotNord],
    chargeEvents: [],
    chargeRate: 148500,
    maintenance: {},
    odometer: 61200, odometerAdjustment: 61190,
    parking: { deviceId: "d2", parkingSpace: "Ladestation A-3", timestamp: new Date(now.getTime() - 8100000) },
    latestRemark: "DC-Schnelllader",
  },
  {
    // Stop icon, ignition on, yellow TT, vehicle status, fault medium — diesel
    id: "d3",
    device: device("d3", "BUS-1003"),
    licensePlate: "M-EC 1003",
    statusData: statusInfo("d3", 48.133, 11.566, 0, false, "01:05:20",
      cockpitData({ speed: 0, rpm: 750, fuel: 45, adBlue: 55, inTemp: 20, outTemp: 14, engineTemp: 102, door1: 1, door2: 0, ignition: 1, odometer: 102340000 }),
    ),
    address: "Arnulfstraße 19, 80335 München",
    zones: "Zone B, Werkstatt",
    ignitionStatus: 1,
    faultName: "Engine Coolant Temperature High",
    faultDescription: "Kühlmitteltemperatur über Schwellenwert",
    faultPriority: 2, faultType: "OEM", faultDId: "diag3",
    isCharging: -1,
    isDriving: false,
    faultInstruction: "Fahrzeug prüfen",
    warnings: [{ type: WarningType.TT_YELLOW, event: demoEvent("d3", RULE_IDS.ttYellow, 3) }],
    chargingInterrupt: false,
    allZones: [ZONES.zoneB, ZONES.werkstatt],
    chargeEvents: [],
    chargeRate: null,
    maintenance: { workOrderJobs: [maintenanceJob(MAINTENANCE_SOURCE.ECO_INSPECTION, 1, 200)] },
    odometer: 102340, odometerAdjustment: 102330,
    latestRemark: "Werkstatttermin vereinbart",
    vehicleStatus: { deviceId: "d3", group: { id: "g1", name: "In Prüfung", color: { a: 255, r: 255, g: 165, b: 0 } } as any },
  },
  {
    // Driving, red TT + PM1 + accident, fault high, maintenance — diesel
    id: "d4",
    device: device("d4", "BUS-1004"),
    licensePlate: "M-EC 1004",
    statusData: statusInfo("d4", 48.155, 11.593, 38, true, "01:23:45",
      cockpitData({ speed: 38, rpm: 2200, fuel: 32, adBlue: 40, inTemp: 23, outTemp: 18, engineTemp: 95, door1: 0, door2: 0, ignition: 1, odometer: 128900000 }),
    ),
    address: "Prinzregentenstraße 7, 80538 München",
    zones: null,
    ignitionStatus: 1,
    faultName: "Brake Pad Wear", faultDescription: "Bremsbelagverschleiß erkannt",
    faultPriority: 3, faultType: "OEM", faultDId: "diag4",
    isCharging: -1,
    isDriving: true,
    faultInstruction: "Bremsbeläge austauschen",
    warnings: [
      { type: WarningType.TT_RED, event: demoEvent("d4", RULE_IDS.ttRed, 1) },
      { type: WarningType.PM1, event: demoEvent("d4", RULE_IDS.pm1, 8) },
      { type: WarningType.AD, event: demoEvent("d4", RULE_IDS.ad, 2) },
    ],
    chargingInterrupt: false,
    allZones: [],
    chargeEvents: [],
    chargeRate: null,
    maintenance: {
      workOrderJobs: [
        maintenanceJob(MAINTENANCE_SOURCE.ECO_INSPECTION, 2, 400), maintenanceJob(MAINTENANCE_SOURCE.ECO_INSPECTION, 3, 100),
        maintenanceJob("Geotab", 1, 300), maintenanceJob("Geotab", 1, 150),
      ],
    },
    odometer: 128900, odometerAdjustment: 128890,
    driverId: "driver2",
  },
  {
    // Charge interrupt, parking brake, seatbelt warning — electric
    id: "d5",
    device: device("d5", "BUS-1005"),
    licensePlate: "M-EC 1005",
    statusData: statusInfo("d5", 48.129, 11.556, 0, false, "04:32:10",
      cockpitData({ speed: 0, kw: 0, soc: 23, inTemp: 15, outTemp: 8, engineTemp: 22, door1: 0, door2: 1, door3: 1, chargingState: 3, ignition: 0, odometer: 45670000, parkingBrake: true, range: 45 }),
    ),
    address: "Theresienhöhe 15, 80339 München",
    zones: "Depot Süd",
    ignitionStatus: 0,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: 3,
    isDriving: false,
    faultInstruction: "",
    warnings: [{ type: WarningType.SB, event: demoEvent("d5", RULE_IDS.sb, 1) }],
    chargingInterrupt: true,
    allZones: [ZONES.depotSued],
    chargeEvents: [],
    chargeRate: 0,
    maintenance: {},
    odometer: 45670, odometerAdjustment: 45660,
    remainingRange: { deviceId: "d5", inputKm: 15, odometerAtInput: 45655000, dateTime: new Date(now.getTime() - 14400000) },
    latestRemark: "Ladeabbruch gemeldet",
  },
  {
    // Driving, EV not charging (grey icon), remaining range — electric
    id: "d6",
    device: device("d6", "BUS-1006"),
    licensePlate: "M-EC 1006",
    statusData: statusInfo("d6", 48.148, 11.572, 55, true, "00:32:05",
      cockpitData({ speed: 55, kw: -45, soc: 71, inTemp: 22, outTemp: 17, engineTemp: 35, door1: 0, door2: 0, chargingState: 0, ignition: 1, odometer: 91200000, range: 280 }),
    ),
    address: "Dachauer Straße 90, 80335 München",
    zones: "Zone A",
    ignitionStatus: 1,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: 0,
    isDriving: true,
    faultInstruction: "",
    warnings: [],
    chargingInterrupt: false,
    allZones: [ZONES.zoneA],
    chargeEvents: [],
    chargeRate: -45000,
    maintenance: {},
    odometer: 91200, odometerAdjustment: 91190,
    remainingRange: { deviceId: "d6", inputKm: 320, odometerAtInput: 91050000, dateTime: new Date(now.getTime() - 7200000) },
    driverId: "driver3",
  },
  {
    // Not communicating, ignition N/A, parking brake, long standing
    id: "d7",
    device: device("d7", "BUS-1007"),
    licensePlate: "M-EC 1007",
    statusData: statusInfo("d7", 48.139, 11.588, 0, false, "1.02:44:30",
      cockpitData({ speed: 0, ignition: 0, odometer: 56780000, parkingBrake: true }),
      false,
    ),
    address: "Rosenheimer Straße 30, 81669 München",
    zones: "Depot Nord",
    ignitionStatus: -1,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: -1,
    isDriving: false,
    faultInstruction: "",
    warnings: [],
    chargingInterrupt: false,
    allZones: [ZONES.depotNord],
    chargeEvents: [],
    chargeRate: null,
    maintenance: {},
    odometer: 56780, odometerAdjustment: 56770,
  },
  {
    // Driving, fault low, ECO open maintenance — diesel
    id: "d8",
    device: device("d8", "BUS-1008"),
    licensePlate: "M-EC 1008",
    statusData: statusInfo("d8", 48.144, 11.558, 15, true, "00:12:40",
      cockpitData({ speed: 15, rpm: 1200, fuel: 88, adBlue: 90, inTemp: 21, outTemp: 15, engineTemp: 82, door1: 0, door2: 0, ignition: 1, odometer: 73890000, calibration: new Date("2026-02-15").getTime() }),
    ),
    address: "Nymphenburger Straße 120, 80636 München",
    zones: null,
    ignitionStatus: 1,
    faultName: "Low Tire Pressure", faultDescription: "Reifendruck zu niedrig",
    faultPriority: 1, faultType: "OEM", faultDId: "diag8",
    isCharging: -1,
    isDriving: true,
    faultInstruction: "Reifendruck prüfen",
    warnings: [],
    chargingInterrupt: false,
    allZones: [],
    chargeEvents: [],
    chargeRate: null,
    maintenance: { workOrderJobs: [maintenanceJob(MAINTENANCE_SOURCE.ECO_INSPECTION, 1, 500)] },
    odometer: 73890, odometerAdjustment: 73880,
    driverId: "driver2",
  },
  {
    // Archived, stop icon, not communicating
    id: "d9",
    device: archivedDevice("d9", "BUS-0900"),
    licensePlate: "M-EC 0900",
    statusData: statusInfo("d9", 48.135, 11.600, 0, false, "30.00:00:00",
      cockpitData({ speed: 0, ignition: 0, odometer: 67400000 }),
      false,
    ),
    address: "Ostbahnhof, 81667 München",
    zones: "Zone B",
    ignitionStatus: 0,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: -1,
    isDriving: false,
    faultInstruction: "",
    warnings: [],
    chargingInterrupt: false,
    allZones: [ZONES.zoneB],
    chargeEvents: [],
    chargeRate: null,
    maintenance: {},
    odometer: 67400, odometerAdjustment: 67390,
  },
  {
    // Parking brake, DC charging, remaining range, parking, vehicle status — electric
    id: "d10",
    device: device("d10", "BUS-1010"),
    licensePlate: "M-EC 1010",
    statusData: statusInfo("d10", 48.151, 11.545, 0, false, "05:20:00",
      cockpitData({ speed: 0, kw: 120, soc: 85, inTemp: 19, outTemp: 11, engineTemp: 28, door1: 0, door2: 0, chargingState: 2, ignition: 0, odometer: 38900000, parkingBrake: true, range: 310 }),
    ),
    address: "Landsberger Straße 302, 80687 München",
    zones: "Depot Süd, Werkstatt",
    ignitionStatus: 0,
    faultName: null, faultDescription: null, faultPriority: 0, faultType: "", faultDId: "",
    isCharging: 2,
    isDriving: false,
    faultInstruction: "",
    warnings: [
      { type: WarningType.PM, event: demoEvent("d10", RULE_IDS.pm, 12) },
      { type: WarningType.TT_YELLOW, event: demoEvent("d10", RULE_IDS.ttYellow, 4) },
    ],
    chargingInterrupt: false,
    allZones: [ZONES.depotSued, ZONES.werkstatt],
    chargeEvents: [],
    chargeRate: 120000,
    maintenance: { workOrderJobs: [maintenanceJob("Geotab", 1, 50)] },
    odometer: 38900, odometerAdjustment: 38890,
    remainingRange: { deviceId: "d10", inputKm: 250, odometerAtInput: 38500000, dateTime: new Date(now.getTime() - 3600000) },
    parking: { deviceId: "d10", parkingSpace: "Stellplatz C-12", timestamp: new Date(now.getTime() - 19200000) },
    vehicleStatus: { deviceId: "d10", group: { id: "g2", name: "Werkstatt", color: { a: 255, r: 244, g: 67, b: 54 } } as any },
  },
];

// ── chart data generation ──

function generateTimeSeries(
  diagnosticId: string,
  deviceId: string,
  baseValue: number,
  variance: number,
  hours = 25,
  points = 100,
) {
  const series = [];
  const step = (hours * 3600000) / points;
  let value = baseValue;
  for (let i = 0; i < points; i++) {
    value = baseValue + (Math.random() - 0.5) * 2 * variance;
    series.push({
      diagnostic: { id: diagnosticId },
      device: { id: deviceId },
      data: Math.round(value * 10) / 10,
      dateTime: now.getTime() - (points - i) * step,
      isLastActive: i === points - 1,
    });
  }
  return series;
}

const CHARTABLE: Record<string, [number, number, number?]> = {
  DiagnosticStateOfChargeId: [65, 20],
  DiagnosticEngineCoolantTemperatureId: [85, 10],
  DiagnosticOutsideTemperatureId: [15, 3],
  DiagnosticElectricVehicleBatteryPowerId: [60000, 50000], // in watts, chart converts to kW
  DiagnosticFuelLevelId: [55, 15],
  DiagnosticEngineSpeedId: [1600, 600],
  DiagnosticCabInteriorTemperatureId: [21, 2],
  DiagnosticCrankingVoltageId: [12.4, 0.3, 336], // ignition voltage — 14 days
  "aFGR6Q9guoUeiaAZI1fD6oQ": [220, 40], // range diagnostic (km)
};

// ── Pre-computed random data (generated once at module load) ──

const CHART_CACHE: Record<string, Record<string, ReturnType<typeof generateTimeSeries>>> = {};
const RANGE_ESTIMATES_CACHE: Record<string, any[]> = {};
const TELLTALE_CACHE: Record<string, any[]> = {};

for (const row of DEMO_DATA) {
  // Charts
  CHART_CACHE[row.id] = {};
  for (const [diagId, [base, variance, hours]] of Object.entries(CHARTABLE)) {
    const hasDiag = diagId === "DiagnosticCrankingVoltageId" ||
      row.statusData?.statusData?.some((s: any) => s.diagnostic.id === diagId);
    if (hasDiag) {
      CHART_CACHE[row.id][diagId] = generateTimeSeries(diagId, row.id, base, variance, hours ?? 25);
    }
  }

  // Range estimates for EVs
  const isEV = row.statusData?.statusData?.some(
    (s: any) => s.diagnostic.id === "aFGR6Q9guoUeiaAZI1fD6oQ",
  );
  if (isEV) {
    const estimates = [];
    for (let m = 11; m >= 0; m--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - m);
      const mean = 200 + Math.random() * 80;
      estimates.push({
        device: { id: row.id },
        vin: (row.device as DeviceWithVehicleInfo).vehicleIdentificationNumber,
        detectionDate: d.toISOString(),
        rangeEstimateMeanKm: mean,
        rangeEstimateLowerBoundKm: mean - 30 - Math.random() * 20,
        rangeEstimateUpperBoundKm: mean + 30 + Math.random() * 20,
        rangeEstimationMethod: "BatteryEstimation",
      });
    }
    RANGE_ESTIMATES_CACHE[row.id] = estimates;
  }

  // Telltales
  TELLTALE_CACHE[row.id] = tellTales
    .filter(t => !t?.name?.includes("Reserved for"))
    .map(tt => sd(tt.id, Math.random() > 0.85 ? (Math.random() > 0.5 ? 1 : 2) : 0));
}

// ── hooks ──

const noop = () => {};

/** Seed all demo data into query cache */
export function seedDemoData(queryClient: QueryClient, activeSessionId: string | null) {
  for (const [id, user] of Object.entries(DEMO_USERS)) {
      queryClient.setQueryData(["user", activeSessionId, id, undefined, undefined], user);
    }

    // PM rules addin data — provides title/description for PM warning details
    queryClient.setQueryData(
      ["addinData", "aDJjNGM4MzQtNjYwMy0wZDk", activeSessionId],
      [
        { addInId: "aDJjNGM4MzQtNjYwMy0wZDk", id: "pm-0", details: {
          ruleId: RULE_IDS.pm, pmTitle: "Bremsenverschleiß erkannt",
          pmDescription: "Die Bremsenabnutzung hat einen kritischen Wert erreicht. Bitte Werkstatttermin vereinbaren.",
        }},
        { addInId: "aDJjNGM4MzQtNjYwMy0wZDk", id: "pm-1", details: {
          ruleId: RULE_IDS.pm1, pmTitle: "Batteriealterung auffällig",
          pmDescription: "Die Batteriealterung zeigt auffällige Werte. Überprüfung empfohlen.",
        }},
      ],
    );

    // Rule name lookups — used by AccidentDetail, FaultInstructions, WarningCell
    const demoRules = {
      pm: { id: RULE_IDS.pm, name: "[PM] Bremsenverschleiß" },
      pm1: { id: RULE_IDS.pm1, name: "[PM1] Batteriealterung" },
      ad: { id: RULE_IDS.ad, name: "[AD] Auffahrunfall" },
      ttYellow: { id: RULE_IDS.ttYellow, name: "[TT] - Gelb" },
      ttRed: { id: RULE_IDS.ttRed, name: "[TT] - Rot" },
      sb: { id: RULE_IDS.sb, name: "[SB] - Anschnallzeichen" },
    };
    queryClient.setQueryData(["rule", activeSessionId, ["[PM]%"]], [[demoRules.pm]]);
    queryClient.setQueryData(["rule", activeSessionId, ["[PM1]%"]], [[demoRules.pm1]]);
    queryClient.setQueryData(["rule", activeSessionId, ["[AD]%"]], [[demoRules.ad]]);
    queryClient.setQueryData(
      ["rule", activeSessionId, ["[TT] - Gelb", "[TT] - Rot", "[SB] - Anschnallzeichen"]],
      [[demoRules.ttYellow], [demoRules.ttRed], [demoRules.sb]],
    );

    // ── Groups (needed by useDiagnosticColumns → useGroupsByParent) ──
    const vehicleStatusGroup = {
      id: "GroupVehicleStatusId", name: "VehicleStatus",
      children: [{ id: "g1" }, { id: "g2" }],
    } as any;
    const parkingGroup = {
      id: "GroupParkingId", name: "Parking",
      children: [{ id: "p1" }, { id: "p2" }],
    } as any;
    const allGroups = [
      vehicleStatusGroup,
      { id: "g1", name: "In Prüfung", color: { a: 255, r: 255, g: 165, b: 0 }, children: [] },
      { id: "g2", name: "Werkstatt", color: { a: 255, r: 244, g: 67, b: 54 }, children: [] },
      parkingGroup,
      { id: "p1", name: "Depot Nord", comments: "Ladestation A-1,Ladestation A-2,Ladestation A-3", children: [] },
      { id: "p2", name: "Depot Süd", comments: "Stellplatz C-10,Stellplatz C-11,Stellplatz C-12", children: [] },
    ];
    queryClient.setQueryData(["group", activeSessionId, "VehicleStatus"], vehicleStatusGroup);
    queryClient.setQueryData(["group", activeSessionId, "Parking"], parkingGroup);
    queryClient.setQueryData(["groups", activeSessionId], allGroups);

    // ── Security filters (needed by useHasPermission) ──
    queryClient.setQueryData(["securityFiltersByGroup", activeSessionId], {});

    // ── User display profile + measurement units ──
    queryClient.setQueryData(["userDisplayProfile", activeSessionId, "demo-user"], null);
    queryClient.setQueryData(["displayMeassurementProfile", undefined], []);

    // ── All AddInData queries (needed by DataGridSettingsProvider, cells, detail panels) ──
    const emptyAddinIds = [
      "azg2MGJmNDYtNTQ1YS0wNGI", // deviceStatusDataTableSettings
      "aDE1ODNjMTgtMGQ3YS0wZTF", // presets
      "ajBkOTEyOTktYjlkMC05NJE", // vehicleStatus
      "a3BhcmtpbmctZGF0YS1lY28", // parking
      "aTZjODdiZDMtMmJjOC04YTF", // remainingRange
      "aTgwODA0ZDYtNzMwZi1iMGQ", // vehicleRemarks
      "aDg2ZGU1NDktNDM3Yi1mYjB", // diagnosticData
      "aDNmNWQwYjYtYjU0Yy02NDF", // rowDetailLayout
      "ajcwODgwODItNTJjMy05NWM", // deviceHistory
    ];
    for (const addInId of emptyAddinIds) {
      queryClient.setQueryData(["addinData", addInId, activeSessionId], []);
    }

    // ── Per-vehicle data (uses pre-computed caches — no random calls here) ──
    for (const row of DEMO_DATA) {
      queryClient.setQueryData(
        ["DeviceStatusInfo", activeSessionId, row.id, COCKPIT_DIAGNOSTICS],
        row.statusData,
      );
      queryClient.setQueryData(
        ["DeviceStatusInfo", activeSessionId, row.id],
        row.statusData,
      );
      queryClient.setQueryData(
        ["DeviceStatusInfo", activeSessionId, row.id, TELLTALES_DIAGNOSTICS],
        { ...row.statusData, statusData: TELLTALE_CACHE[row.id] },
      );
      queryClient.setQueryData(["device", activeSessionId, row.id], row.device);

      if (RANGE_ESTIMATES_CACHE[row.id]) {
        queryClient.setQueryData(["rangeEstimate", activeSessionId, row.id], RANGE_ESTIMATES_CACHE[row.id]);
      }

      for (const [diagId, series] of Object.entries(CHART_CACHE[row.id] ?? {})) {
        queryClient.setQueryData(["statusDataDevice", activeSessionId, diagId, row.id], series);
      }
    }
}

// ── Pre-computed data for cache subscriber ──

const DEMO_FAULTS: Record<string, any[]> = {};
const DEMO_DIAGNOSTICS: Record<string, any> = {};
const DEMO_VINS: Record<string, any> = {};

for (const row of DEMO_DATA) {
  if (row.faultName) {
    DEMO_FAULTS[row.id] = [{
      id: `fault-${row.id}`,
      device: { id: row.id },
      diagnostic: { id: row.faultDId, name: row.faultName },
      faultState: row.faultPriority >= 2 ? "Active" : "Pending",
      dateTime: new Date(now.getTime() - 3600000),
      count: 1,
      controller: { id: "ctrl-1", name: "Body" },
      failureModeId: { id: "fm-1" },
    }];
    DEMO_DIAGNOSTICS[row.faultDId] = {
      id: row.faultDId, name: row.faultName,
      source: { id: "src-1", name: row.faultType || "OEM" },
    };
  }
  const vin = (row.device as DeviceWithVehicleInfo).vehicleIdentificationNumber;
  if (vin) {
    DEMO_VINS[vin] = {
      vin, make: "Mercedes-Benz", model: "eCitaro", vehicleType: "Bus",
      company: "Daimler Buses", manufacturer: "EvoBus GmbH", year: "2024",
    };
  }
}

const subscribedClients = new WeakSet<QueryClient>();

export function useDemoData() {
  const queryClient = useQueryClient();
  const { activeSessionId } = useGeotabClient();

  // Subscribe to cache to seed faultData queries (they have dynamic date keys)
  if (!subscribedClients.has(queryClient)) {
    subscribedClients.add(queryClient);
    queryClient.getQueryCache().subscribe(event => {
      if (event.type !== "added") return;
      const key = event.query.queryKey;
      const type = key[0] as string;

      if (type === "faultData") {
        const deviceIds = key[2] as string[] | undefined;
        if (deviceIds?.length) {
          queryClient.setQueryData(key, deviceIds.flatMap(id => DEMO_FAULTS[id] ?? []));
        }
      } else if (type === "diagnostics") {
        const ids = key[2] as string[] | undefined;
        if (ids?.length) {
          const result: Record<string, any> = {};
          for (const id of ids) if (DEMO_DIAGNOSTICS[id]) result[id] = DEMO_DIAGNOSTICS[id];
          queryClient.setQueryData(key, result);
        }
      } else if (type === "vins") {
        const vins = key[2] as string[] | undefined;
        if (vins?.length) {
          queryClient.setQueryData(key, vins.map(v => DEMO_VINS[v] ?? { vin: v, make: "", model: "", vehicleType: "", company: "", manufacturer: "", year: "" }));
        }
      }
    });
  }

  seedDemoData(queryClient, activeSessionId);
  const data = DEMO_DATA;
  return {
    data,
    total: data.length,
    refetchDevice: noop,
    refetchStatusData: noop,
    drivingDevices: data.filter(d => d.isDriving).length,
    chargingDevices: data.filter(d => d.isCharging === 1 || d.isCharging === 2).length,
    chargeInterruptDevices: data.filter(d => d.chargingInterrupt).length,
    withPMDevices: data.filter(d => d.warnings.some(w => w.type === WarningType.PM || w.type === WarningType.PM1)).length,
    withStatusDevices: data.filter(d => d.vehicleStatus).length,
    notChargingDevices: data.filter(d => d.isCharging === 0).length,
  };
}
